import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { QrCode, Link2, ExternalLink, Gift, Info, Loader2, ShieldCheck, AlertCircle, CheckCircle2, Smartphone, PlusCircle } from "lucide-react";
import { userApi, errMsg } from "@/lib/api";
import { fmtMoney, fmtDate, newRequestId, METHOD_TYPE_LABEL } from "@/lib/format";
import { StatusBadge, CopyButton, QrImage, FileUploader, EmptyState, PageHeader, useDebounced } from "@/components/common";
import { StatusTimeline, DepositSummary } from "@/components/DepositDetailsSheet";
import DepositDetailsSheet from "@/components/DepositDetailsSheet";
import DepositHero from "@/components/DepositHero";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

const QUICK = { INR: [500, 1000, 5000, 10000], USDT: [50, 100, 500, 1000] };

const Step = ({ n, title, children, muted }) => (
  <section className={cn("rounded-xl border bg-white p-4 sm:p-5", muted && "opacity-60")}>
    <div className="mb-4 flex items-center gap-3">
      <span className="num flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{n}</span>
      <h2 className="text-base font-semibold">{title}</h2>
    </div>
    {children}
  </section>
);

function BonusPreview({ preview, loading, currency, amount }) {
  if (!amount) return <p className="text-sm text-muted-foreground">Enter an amount to see your bonus.</p>;
  if (loading || !preview) return <Skeleton className="h-28" />;
  return (
    <div className="space-y-2 text-sm" data-testid="bonus-preview">
      <div className="flex justify-between"><span className="text-muted-foreground">Deposit</span><span className="num" data-testid="bonus-preview-amount">{fmtMoney(preview.amount, currency)}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Bonus rate</span><span className="num">{preview.eligible ? `${preview.rate_pct}%` : "—"}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Eligible amount</span><span className="num" data-testid="bonus-preview-eligible">{fmtMoney(preview.eligible_amount, currency)}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Bonus</span><span className="num font-medium text-emerald-700" data-testid="bonus-preview-bonus">+{fmtMoney(preview.bonus_amount, currency)}</span></div>
      <div className="flex justify-between border-t pt-2 font-semibold"><span>Total credit after approval</span><span className="num" data-testid="bonus-preview-total">{fmtMoney(preview.total_credit, currency)}</span></div>
      {!preview.eligible && <p className="rounded-lg bg-zinc-100 px-3 py-2 text-xs text-zinc-700">Bonus campaign is {preview.state} for {currency}.</p>}
      {preview.eligible && preview.amount > preview.max_eligible && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">Bonus applies to the first {fmtMoney(preview.max_eligible, currency)} of this deposit.</p>}
      {preview.campaign_end && <p className="text-xs text-muted-foreground">Campaign {fmtDate(preview.campaign_start, false)} – {fmtDate(preview.campaign_end, false)}</p>}
    </div>
  );
}

function SubmittedView({ dep, onNew }) {
  const { data } = useQuery({ queryKey: ["deposits", "detail", dep.id], queryFn: () => userApi.get(`/deposits/${dep.id}`).then((r) => r.data), refetchInterval: 8000, initialData: dep });
  const d = data || dep;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-2xl" data-testid="deposit-submitted-view">
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-6 w-6 text-emerald-600" />
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-semibold">Deposit request submitted</h2>
              <p className="mt-1 text-sm text-muted-foreground">We'll verify your payment and notify you here. Status updates automatically.</p>
            </div>
            <span className="w-full pl-9 sm:ml-auto sm:w-auto sm:pl-0"><StatusBadge status={d.status} /></span>
          </div>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <DepositSummary d={d} />
            <div className="rounded-xl bg-secondary p-4"><p className="mb-3 text-sm font-semibold">Live status</p><StatusTimeline deposit={d} /></div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button onClick={onNew} className="btn-lift gap-2" data-testid="deposit-new-request-button"><PlusCircle className="h-4 w-4" /> New deposit</Button>
            <Button asChild variant="outline" data-testid="deposit-view-history-button"><Link to="/transactions">View history</Link></Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function Deposit() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("upi_qr");
  const [currency, setCurrency] = useState("INR");
  const [methodId, setMethodId] = useState(null);
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [paid, setPaid] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [clientId, setClientId] = useState(newRequestId());
  const [formErr, setFormErr] = useState("");
  const [openDep, setOpenDep] = useState(null);

  const methodsQ = useQuery({
    queryKey: ["methods"],
    queryFn: () => userApi.get("/payment-methods").then((r) => r.data),
    refetchInterval: 20000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  const recentQ = useQuery({ queryKey: ["deposits", "recent-5"], queryFn: () => userApi.get("/deposits", { params: { limit: 5 } }).then((r) => r.data), refetchInterval: 20000 });
  const all = useMemo(() => methodsQ.data || [], [methodsQ.data]);
  const tabCurrencies = useMemo(() => ["INR", "USDT"].filter((c) => all.some((m) => m.method_type === tab && m.currency === c)), [all, tab]);
  const methods = all.filter((m) => m.method_type === tab && m.currency === currency);
  const method = methods.find((m) => m.id === methodId);

  useEffect(() => {
    if (tabCurrencies.length && !tabCurrencies.includes(currency)) { setCurrency(tabCurrencies[0]); setMethodId(null); }
  }, [tabCurrencies]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (methodId && !methods.find((m) => m.id === methodId)) {
      // the selected method disappeared after a refresh: admin deactivated / archived it
      if (all.length && !all.some((m) => m.id === methodId)) toast.info("Payment options were updated by our team");
      setMethodId(methods[0]?.id || null);
    } else if (!methodId && methods.length) setMethodId(methods[0].id);
  }, [methods.map((m) => m.id).join(","), currency, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const amt = parseFloat(amount);
  const amountError = !amount ? null : isNaN(amt) || amt <= 0 ? "Enter a valid amount" : method && amt < method.min_amount ? `Minimum is ${fmtMoney(method.min_amount, currency)}` : method && amt > method.max_amount ? `Maximum is ${fmtMoney(method.max_amount, currency)}` : null;
  const amountOk = !!method && amt > 0 && !amountError;
  const debAmt = useDebounced(amountOk ? amt : 0, 300);
  const previewQ = useQuery({
    queryKey: ["preview", currency, debAmt],
    queryFn: () => userApi.post("/deposits/preview", { currency, amount: debAmt }).then((r) => r.data),
    enabled: debAmt > 0,
  });
  const refOk = reference.trim().length >= 6 && /^[A-Za-z0-9\-_/.]+$/.test(reference.trim());
  const canSubmit = amountOk && refOk && paid && !submitting;

  const resetForm = () => {
    setAmount(""); setReference(""); setReceipt(null); setPaid(false); setFormErr(""); setClientId(newRequestId());
  };

  const uploadReceipt = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await userApi.post("/uploads/receipt", fd);
      setReceipt(r.data);
      toast.success("Receipt uploaded");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
      throw e;
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setFormErr("");
    try {
      const r = await userApi.post("/deposits", {
        payment_method_id: method.id, amount: amt, transaction_reference: reference.trim(),
        receipt_file_id: receipt?.file_id || null, client_request_id: clientId,
      });
      setSubmitted(r.data);
      toast.success("Deposit request submitted", { description: `Request ${r.data.id} is pending verification.` });
      resetForm();
      qc.invalidateQueries({ queryKey: ["deposits"] });
      qc.invalidateQueries({ queryKey: ["wallets"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const m = errMsg(e);
      setFormErr(m);
      toast.error(m);
      qc.invalidateQueries({ queryKey: ["methods"] });
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  if (submitted) return <SubmittedView dep={submitted} onNew={() => setSubmitted(null)} />;

  return (
    <div>
      <PageHeader title="Deposit funds" subtitle="Pay with an active method, share your reference, and we'll credit your wallet after verification." />

      <DepositHero
        amount={amountOk ? amt : 0}
        currency={currency}
        bonus={previewQ.data?.bonus_amount || 0}
        method={method}
      />

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setMethodId(null); }} className="mb-5">
        <TabsList className="grid h-11 w-full grid-cols-2 sm:w-[420px]">
          <TabsTrigger value="upi_qr" className="gap-2" data-testid="deposit-tab-qr"><QrCode className="h-4 w-4" /> QR code payment</TabsTrigger>
          <TabsTrigger value="payment_link" className="gap-2" data-testid="deposit-tab-link"><Link2 className="h-4 w-4" /> Payment link</TabsTrigger>
        </TabsList>
      </Tabs>

      {methodsQ.isLoading ? <Skeleton className="h-72 rounded-xl" /> : methodsQ.isError ? (
        <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertTitle>Could not load payment methods</AlertTitle><AlertDescription>Check your connection and <button className="underline" onClick={() => methodsQ.refetch()}>retry</button>.</AlertDescription></Alert>
      ) : tabCurrencies.length === 0 ? (
        <EmptyState icon={tab === "upi_qr" ? QrCode : Link2} title={`No ${tab === "upi_qr" ? "QR code" : "payment link"} methods are active right now`} testId="deposit-no-methods">
          Please try the other option or check back shortly. This page updates automatically when our team enables a method.
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-7">
            <Step n="1" title="Choose currency & method">
              <div className="mb-4 inline-flex rounded-lg border bg-secondary p-1">
                {["INR", "USDT"].map((c) => (
                  <button key={c} disabled={!tabCurrencies.includes(c)} onClick={() => { setCurrency(c); setMethodId(null); setAmount(""); }} data-testid={`deposit-currency-${c.toLowerCase()}`}
                    className={cn("rounded-md px-4 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40", currency === c ? "bg-white text-primary shadow-sm" : "text-muted-foreground")}>
                    {c}
                  </button>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {methods.map((m) => (
                  <button key={m.id} onClick={() => setMethodId(m.id)} data-testid="deposit-method-card" data-method-id={m.id}
                    className={cn("card-hover rounded-xl border p-4 text-left", methodId === m.id ? "border-primary bg-teal-50/60 ring-1 ring-primary" : "bg-white")}>
                    <div className="flex items-center gap-2">
                      {m.method_type === "upi_qr" ? <QrCode className="h-4 w-4 text-primary" /> : <Link2 className="h-4 w-4 text-primary" />}
                      <p className="font-medium">{m.name}</p>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">{METHOD_TYPE_LABEL[m.method_type]} · {m.currency}</p>
                    <p className="num mt-1 text-xs text-muted-foreground">{fmtMoney(m.min_amount, m.currency)} – {fmtMoney(m.max_amount, m.currency)}</p>
                  </button>
                ))}
              </div>
            </Step>

            <Step n="2" title="Enter amount" muted={!method}>
              {!method && <p className="mb-3 text-sm text-muted-foreground" data-testid="deposit-select-method-hint">Select a payment method above to enter an amount.</p>}
              <Label htmlFor="amt" className="sr-only">Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg text-muted-foreground">{currency === "INR" ? "₹" : "₮"}</span>
                <Input id="amt" inputMode="decimal" placeholder="0.00" value={amount} disabled={!method}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"))}
                  className="num h-14 pl-9 pr-16 text-2xl" data-testid="deposit-amount-input" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">{currency}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {QUICK[currency].map((v) => (
                  <Button key={v} type="button" size="sm" variant="outline" disabled={!method} onClick={() => setAmount(String(v))} data-testid={`deposit-quick-amount-${v}`}>{fmtMoney(v, currency).replace(".00", "").replace(" USDT", "")}</Button>
                ))}
              </div>
              {amountError ? <p className="mt-2 text-sm text-rose-700" data-testid="deposit-amount-error">{amountError}</p>
                : method && <p className="mt-2 text-xs text-muted-foreground">Limits for {method.name}: {fmtMoney(method.min_amount, currency)} – {fmtMoney(method.max_amount, currency)}</p>}
            </Step>

            <AnimatePresence>
              {method && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <Step n="3" title={method.method_type === "upi_qr" ? "Scan & pay" : "Open link & pay"} muted={!amountOk}>
                    {!amountOk && <p className="mb-3 text-sm text-muted-foreground">Enter a valid amount to reveal payment details.</p>}
                    <div className={cn(!amountOk && "pointer-events-none blur-[3px]")} data-testid="deposit-payment-panel">
                      {method.method_type === "upi_qr" ? (
                        <div className="grid gap-5 sm:grid-cols-[220px_1fr]">
                          <QrImage url={method.qr_image_url} className="w-full max-w-[260px]" testId="deposit-qr-image" />
                          <div className="space-y-3">
                            <div className="rounded-lg bg-secondary p-3">
                              <p className="text-xs text-muted-foreground">Pay exactly</p>
                              <p className="num text-2xl font-semibold" data-testid="deposit-pay-exact-amount">{amountOk ? fmtMoney(amt, currency) : "—"}</p>
                            </div>
                            {method.account_details && (
                              <div className="flex items-center justify-between gap-2 rounded-lg border p-3">
                                <div className="min-w-0"><p className="text-xs text-muted-foreground">UPI ID / account</p><p className="num truncate text-sm font-medium" data-testid="deposit-account-details">{method.account_details}</p></div>
                                <CopyButton value={method.account_details} label="Copy" testId="deposit-copy-account-button" />
                              </div>
                            )}
                            {method.payment_url && (
                              <Button asChild variant="outline" className="w-full gap-2" data-testid="deposit-open-upi-app-button"><a href={method.payment_url} target="_blank" rel="noopener noreferrer"><Smartphone className="h-4 w-4" /> Open in UPI app</a></Button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="rounded-xl border bg-secondary p-4">
                            <p className="text-xs text-muted-foreground">Payment link</p>
                            <p className="num mt-1 break-all text-sm font-medium" data-testid="deposit-payment-link-url">{method.payment_url}</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button asChild className="btn-lift gap-2" data-testid="deposit-open-payment-link-button"><a href={method.payment_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /> Open payment link</a></Button>
                              <CopyButton value={method.payment_url} label="Copy link" testId="deposit-copy-link-button" size="default" />
                            </div>
                          </div>
                          <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">Pay <span className="num font-semibold">{amountOk ? fmtMoney(amt, currency) : "—"}</span> on the provider page, then come back here and enter the transaction reference.</div>
                          {method.account_details && <div className="flex items-center justify-between rounded-lg border p-3 text-sm"><span className="num truncate">{method.account_details}</span><CopyButton value={method.account_details} label="Copy" testId="deposit-copy-account-button" /></div>}
                        </div>
                      )}
                      {method.instructions && (
                        <div className="mt-4 flex gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900" data-testid="deposit-method-instructions">
                          <Info className="mt-0.5 h-4 w-4 shrink-0" /><p className="whitespace-pre-line">{method.instructions}</p>
                        </div>
                      )}
                    </div>
                  </Step>
                </motion.div>
              )}
            </AnimatePresence>

            <Step n="4" title="Confirm your payment" muted={!amountOk}>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ref">Transaction reference (UTR / txn ID)</Label>
                  <Input id="ref" value={reference} disabled={!amountOk} onChange={(e) => setReference(e.target.value)} placeholder="e.g. 412345678901" className="num h-11" maxLength={64} data-testid="deposit-reference-input" />
                  {reference && !refOk && <p className="text-xs text-rose-700" data-testid="deposit-reference-error">Use at least 6 letters/numbers (allowed: - _ / .)</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>Payment receipt <span className="text-muted-foreground">(optional, speeds up review)</span></Label>
                  <FileUploader value={receipt} onUpload={uploadReceipt} onClear={() => setReceipt(null)} />
                </div>
                <label className="flex items-start gap-3 rounded-lg border p-3 text-sm">
                  <Checkbox checked={paid} onCheckedChange={(v) => setPaid(!!v)} disabled={!amountOk} data-testid="deposit-paid-checkbox" className="mt-0.5" />
                  <span>I have completed this payment of <span className="num font-medium">{amountOk ? fmtMoney(amt, currency) : "—"}</span> and the reference above is correct.</span>
                </label>
              </div>
            </Step>
          </div>

          <div className="lg:col-span-5">
            <div className="space-y-4 lg:sticky lg:top-24">
              <Card>
                <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><Gift className="h-4 w-4 text-amber-700" /> Bonus & summary</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <BonusPreview preview={previewQ.data} loading={previewQ.isFetching && !previewQ.data} currency={currency} amount={amountOk ? amt : 0} />
                  {method && <div className="rounded-lg bg-secondary px-3 py-2 text-xs text-muted-foreground">Method: <span className="font-medium text-foreground">{method.name}</span></div>}
                  {formErr && <Alert variant="destructive" data-testid="deposit-form-error"><AlertCircle className="h-4 w-4" /><AlertDescription>{formErr}</AlertDescription></Alert>}
                  <Button className="btn-lift h-12 w-full text-base" disabled={!canSubmit} onClick={() => setConfirmOpen(true)} data-testid={tab === "upi_qr" ? "deposit-qr-submit-request-button" : "deposit-link-submit-request-button"}>
                    Submit deposit request
                  </Button>
                  <p className="flex items-start gap-2 text-xs text-muted-foreground"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /> Your wallet is credited only after our team verifies the payment. Receipts alone are never used to credit funds.</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Your recent requests</CardTitle></CardHeader>
                <CardContent>
                  {recentQ.data?.items?.length === 0 && <p className="text-sm text-muted-foreground">No requests yet.</p>}
                  <ul className="divide-y">
                    {recentQ.data?.items?.map((d) => (
                      <li key={d.id}><button onClick={() => setOpenDep(d.id)} className="flex w-full items-center gap-3 py-2.5 text-left" data-testid="deposit-recent-item">
                        <div className="min-w-0 flex-1"><p className="num text-sm font-medium">{fmtMoney(d.amount, d.currency)}</p><p className="truncate text-xs text-muted-foreground">{d.id} · {fmtDate(d.created_at)}</p></div>
                        <StatusBadge status={d.status} />
                      </button></li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="deposit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm deposit request</AlertDialogTitle>
            <AlertDialogDescription>Please check the details. Our team will verify the payment before crediting.</AlertDialogDescription>
          </AlertDialogHeader>
          {method && previewQ.data && (
            <div className="space-y-2 rounded-lg bg-secondary p-4 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Method</span><span>{method.name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="num">{fmtMoney(amt, currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Bonus</span><span className="num text-emerald-700" data-testid="deposit-confirm-bonus">+{fmtMoney(previewQ.data.bonus_amount, currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Reference</span><span className="num">{reference.trim()}</span></div>
              <div className="flex justify-between border-t pt-2 font-semibold"><span>Credit after approval</span><span className="num">{fmtMoney(previewQ.data.total_credit, currency)}</span></div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deposit-confirm-cancel">Back</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); submit(); }} disabled={submitting} data-testid="deposit-confirm-submit">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirm & submit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <DepositDetailsSheet id={openDep} onClose={() => setOpenDep(null)} />
    </div>
  );
}
